import React, {useEffect, useState} from 'react';
import {RootState} from "../../../../../reducers";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getPromotions
} from "../../../../services/Menu/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {connect} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {OrderType} from "../../../../services/Api/types";
import {useLocation} from "react-router-dom";
import {DateHelper} from "../../../../services/Common/Date/DateHelper";
import {anyMenuHasPickDate} from "../../../../components/Cart/utils";
import CategoryHelper from "../../../../services/Menu/services/Category/CategoryHelper";
import {getParsedSessionOrderType} from "../../../../utils/order";
import PromotionHelper from "../../../../services/Menu/services/Promotion/PromotionHelper";
import {getCart} from "../../../../services/Cart/reducer";
import ItemAvailabilityHelper from "../../../../services/Menu/services/Item/ItemAvailabilityHelper";
import {ReactComponent as ArrowRightIco} from "../../../../../images/icons/arrow-right.svg";
import CartChangeOrderDetailsModal
    from "../../../../components/Cart/components/CartChangeOrderDetailsModal/CartChangeOrderDetailsModal";

interface OwnProps {
    product: Record<string, any>;
    setIsProductAvailable: React.Dispatch<React.SetStateAction<boolean>>;
    productView: Record<string, any>;
    productItemGroup: Record<string, any> | undefined;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductAvailability = ({
                                 availabilities,
                                 items,
                                 product,
                                 session,
                                 locale,
                                 config,
                                 menus,
                                 categories,
                                 promotions,
                                 cart,
                                 itemGroups,
                                 setIsProductAvailable,
                                 productView,
                                 productItemGroup
                             }: Props) => {
    const availability = AvailableHelper.getAvailability(availabilities, product.availability_id);
    const isOpen = availability ? HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(session.pickup_at)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)) : true;
    const location = useLocation();
    const categoryIdFromUrlState = location.state?.categoryId;
    const categoryId = categoryIdFromUrlState ?? ItemAvailabilityHelper.getCategoryWithNearestAvailableDateForItem(product, categories, menus, session, productItemGroup)?.id;
    const categoryById = categories.find((c: Record<string, any>) => c.status === 'ENABLED' && categoryId === c.id);
    const {t} = useTranslation();
    let categoryMenus: Record<string, any>[] = [];
    const parsedCurrentType = getParsedSessionOrderType(session.order_type as OrderType);
    const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);

    const getParsedCategory = () => {
        let parsedCategory: Record<string, any> | undefined = undefined;

        if (!categoryById) return parsedCategory;

        const availablePromotions = PromotionHelper.getActivePromotionsForCategory(promotions, cart.total);
        parsedCategory = CategoryHelper.getParsedCategories([categoryById], items, availabilities, availablePromotions, session, cart, itemGroups, menus)[0];

        if (!parsedCategory || parsedCategory.entities.length === 0) return undefined;

        const menusWithCategory = CategoryHelper.getMenusForCategory(menus, parsedCategory);
        const menuAvailabilities = AvailableHelper.getAvailabilities(availabilities, menusWithCategory.map((menu: any) => menu.availability_id));
        categoryMenus = [...menusWithCategory];
        return menuAvailabilities.length > 0 ? parsedCategory : undefined;
    }

    const finalCategory = getParsedCategory();

    const menusWithCategoryForCurrentOrderType = categoryMenus.filter((menu: any) => menu[parsedCurrentType.toLowerCase()] === true);
    const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: any) => menu.availability_id));
    const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(session.pickup_at));
    const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));
    const isCategoryAvailable = isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;
    const availableCategoryItems = CategoryHelper.getAvailableCategoryItems(availabilities, session.pickup_at, finalCategory?.entities || []);
    const isProductInAvailableCategoryItems = !!availableCategoryItems.find((item: Record<string, any>) => {
        if (item.type === "ITEM_GROUP") {
            return item.items?.some((itemItem: Record<string, any>) => itemItem.item_id === product.id) || item.item.id === product.id;
        }

        return item.id === product.id;
    });
    const isCategoryFinallyAvailable = isCategoryAvailable ? isCategoryAvailable && !!availableCategoryItems.length : isCategoryAvailable;
    const isAvailable = isCategoryFinallyAvailable && isOpen && !productView.disabled && isProductInAvailableCategoryItems;

    useEffect(() => {
        setIsProductAvailable(isAvailable);
    }, [isAvailable, setIsProductAvailable]);

    if (!finalCategory) return (
        <div className="product-availability-info-container d-flex flex-column w-100">
            <p className="card-text text-warning fw-normal product-availability-info w-100">
                {t("common.word.not_available")}
            </p>
        </div>
    );

    if (isAvailable || !config) return <></>;

    const productAvailabilityInfos = ItemAvailabilityHelper.getItemAvailabilityInfo(product, availability, config, menus, availabilities, categoryById, session.pickup_at, session.order_type as OrderType, locale, t);
    const categoryAvailabilityInfos = CategoryHelper.getAvailabilityInfo(finalCategory, categoryMenus, finalCategory.entities, availabilities, config, session.order_type as OrderType, session.pickup_at, t, locale, true);
    const finalAvailabilityInfos = (isCategoryFinallyAvailable && !isProductInAvailableCategoryItems) || (!isCategoryFinallyAvailable && !isProductInAvailableCategoryItems) ?
        productAvailabilityInfos : categoryAvailabilityInfos ?? productAvailabilityInfos;

    const getAvailabilityDescription = () => {
        const canChangePickupAt = config?.ordering?.pick_date && anyMenuHasPickDate(categoryMenus, parsedCurrentType)
        const infoForCurrentOrderType = finalAvailabilityInfos.find(info => info.includeCurrentType || info.currentType);
        const infoForOtherOrderTypes = finalAvailabilityInfos.filter(info => !info.includeCurrentType && !info.currentType);

        if (infoForCurrentOrderType && infoForOtherOrderTypes.length > 0 && !infoForCurrentOrderType.isNowAvailable && infoForOtherOrderTypes.every(info => !info.isNowAvailable) && canChangePickupAt) {
            return t("modules.menu.field.change_pickup_date.title");
        }

        if (infoForCurrentOrderType && !infoForCurrentOrderType.isNowAvailable && infoForOtherOrderTypes.some(info => info.isNowAvailable)) {
            return t("modules.menu.field.change_pickup_date_or_order_type.title");
        }

        if (!infoForCurrentOrderType && infoForOtherOrderTypes.length > 0 && infoForOtherOrderTypes.every(info => !info.isNowAvailable)) {
            return t("modules.menu.field.change_pickup_date_or_order_type.title");
        }

        if (!infoForCurrentOrderType && infoForOtherOrderTypes.some(info => info.isNowAvailable)) {
            return t("modules.menu.field.change_order_type.title");
        }

        if (infoForCurrentOrderType && infoForOtherOrderTypes.length === 0 && !infoForCurrentOrderType.isNowAvailable && canChangePickupAt) {
            return t("modules.menu.field.change_pickup_date.title");
        }

        return "";
    }

    const parsedInfos = ItemAvailabilityHelper.getParsedAvailabilityInfos(finalAvailabilityInfos);
    const availabilityDescription = getAvailabilityDescription();

    if (parsedInfos.length === 0) return <></>;

    return (
        <>
            <div
                className={`product-availability-info-container d-flex w-100 align-items-center ${availabilityDescription ? "cursor-pointer" : ""}`}
                onClick={availabilityDescription ? () => setShowOrderDetailsModal(true) : undefined}
            >
                <div className="d-flex flex-column w-100">
                    <p className="text-warning fw-normal mb-0">
                        {parsedInfos.join(". ")}
                    </p>
                    {availabilityDescription && (
                        <p className="fw-normal mb-0 description">
                            {availabilityDescription}
                        </p>
                    )}
                </div>
                {availabilityDescription && <ArrowRightIco/>}
            </div>
            {
                showOrderDetailsModal && <CartChangeOrderDetailsModal show={showOrderDetailsModal}
                                                                      onHide={() => setShowOrderDetailsModal(false)}/>
            }
        </>
    )
}
const mapStateToProps = (state: RootState) => ({
    availabilities: getAvailabilities(state.menu),
    session: getSession(state.session),
    locale: selectLocale(state.session),
    config: getStoreConfig(state.config),
    menus: getMenus(state.menu),
    items: getItems(state.menu),
    categories: getCategories(state.menu),
    promotions: getPromotions(state.menu),
    cart: getCart(state.cart),
    itemGroups: getItemGroups(state.menu),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ProductAvailability));
